/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from "react"
import PropTypes from "prop-types"
import { useStaticQuery, graphql } from "gatsby"
import { getSrc } from "gatsby-plugin-image"
import StructuredData from "./structured-data"
import { buildStandardPageSchemaNodes } from "./schema-utils"
import { buildCanonicalUrl } from "../utils/url"

function SEO({
  description,
  lang,
  title,
  noindex,
  canonical,
  pathname,
  schemaNodes,
  schemaBuilder,
}) {
  const { site, defaultSocialImage } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            siteUrl
            description
            author
          }
        }
        defaultSocialImage: file(
          relativePath: { eq: "Spass_beim_BJJ_in_Muenchen.jpg" }
        ) {
          childImageSharp {
            gatsbyImageData(
              width: 1200
              height: 630
              layout: FIXED
              quality: 78
              formats: [JPG]
              placeholder: NONE
              transformOptions: { fit: COVER, cropFocus: CENTER }
            )
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata.description
  const siteUrl = site.siteMetadata.siteUrl
  const canonicalTarget = canonical || pathname
  const canonicalUrl = canonicalTarget
    ? buildCanonicalUrl(canonicalTarget, siteUrl)
    : null
  const defaultSocialImageSrc = getSrc(
    defaultSocialImage?.childImageSharp?.gatsbyImageData
  )
  const socialImageUrl = defaultSocialImageSrc
    ? new URL(defaultSocialImageSrc, siteUrl).toString()
    : null
  const defaultSchemaNodes = buildStandardPageSchemaNodes({
    siteUrl,
      path: canonicalUrl || pathname || "/",
      title,
      description: metaDescription,
      image: socialImageUrl,
  })
  const customSchemaNodes =
    typeof schemaBuilder === "function"
      ? schemaBuilder({
          canonicalUrl,
          description: metaDescription,
          lang,
          siteUrl,
          title,
          defaultSchemaNodes,
        })
      : schemaNodes
  const finalSchemaNodes = customSchemaNodes || defaultSchemaNodes

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={metaDescription} />
      {noindex && <meta name="robots" content="noindex, follow" />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Team Laurien & Alex" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={metaDescription} />
      {socialImageUrl ? (
        <>
          <meta property="og:image" content={socialImageUrl} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
        </>
      ) : null}
      <meta property="og:locale" content={lang === "en" ? "en_US" : "de_DE"} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={site.siteMetadata.author} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDescription} />
      {socialImageUrl ? (
        <meta name="twitter:image" content={socialImageUrl} />
      ) : null}
      <StructuredData nodes={finalSchemaNodes} />
    </>
  )
}

SEO.defaultProps = {
  lang: `de`,
  meta: [],
  description: ``,
  noindex: false,
  canonical: null,
  pathname: null,
  schemaBuilder: null,
  schemaNodes: null,
}

SEO.propTypes = {
  canonical: PropTypes.string,
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  noindex: PropTypes.bool,
  pathname: PropTypes.string,
  schemaBuilder: PropTypes.func,
  schemaNodes: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.object,
  ]),
  title: PropTypes.string.isRequired,
}

export default SEO
